import axios, { AxiosError } from 'axios';
import { jwtDecode } from "jwt-decode";
import { baseUrl } from "./Const";
import { cacheData, getCachedData, queueSyncAction, getQueuedActions, getIdMapping } from "./indexedDb";
import { toast } from "react-toastify";

const api = axios.create({
  baseURL: baseUrl,
  timeout: 30000,
});

/* ─────────────────────────── helpers ─────────────────────────── */

export const LOCAL_ID_RE = /offline_ord_\d+/;
export const isLocalId = (id) => typeof id === "string" && LOCAL_ID_RE.test(id);

export const currentRestaurantId = () => {
  try {
    return jwtDecode(localStorage.getItem("token"))?.user?.id || null;
  } catch {
    return null;
  }
};

// Cache keys are per restaurant so a shared device never shows another outlet's data
const scoped = (key) => `${currentRestaurantId() || "anon"}:${key}`;

// "orders/123?x=1" — relative path without base URL or leading slash
const pathOf = (config) => {
  let url = config?.url || "";
  const base = config?.baseURL || "";
  if (base && url.startsWith(base)) url = url.slice(base.length);
  return url.replace(/^https?:\/\/[^/]+/, "").replace(/^\/+/, "");
};

const parseBody = (config) => {
  const data = config?.data;
  if (!data) return {};
  if (typeof data === "string") {
    try { return JSON.parse(data); } catch { return {}; }
  }
  return data;
};

// GET endpoints the POS needs to keep working offline
const CACHEABLE_GET = [
  /^layout$/,
  /^orders\/ongoing$/,
  /^products\/[^/]+$/,
  /^get-products\/[^/]+$/,
  /^get-product-data\/[^/]+$/,
  /^newcategories\/[^/]+$/,
  /^categories\/[^/]+$/,
  /^subscriptions\/status$/,
  /^happy-hours\/active$/,
  /^combos$/,
  /^user\/[a-f\d]{24}$/i,
  /^customers\/check\/[^/]+$/,
];

const NON_ORDER_SEGMENTS = ["active", "ongoing", "kitchen", "parcel", "select-table"];

const cacheKeyFor = (config) => {
  const [path, query = ""] = pathOf(config).split("?");
  if (path === "orders/active") return "active_orders";

  const orderMatch = path.match(/^orders\/([^/]+)$/);
  if (orderMatch && !NON_ORDER_SEGMENTS.includes(orderMatch[1])) return `order_${orderMatch[1]}`;

  if (!CACHEABLE_GET.some((re) => re.test(path))) return null;

  // Params are part of the key so paginated/filtered lists never overwrite each other
  const params = new URLSearchParams(query);
  Object.entries(config.params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) params.append(k, v);
  });
  params.sort();
  const qs = params.toString();
  return `get:${path}${qs ? `?${qs}` : ""}`;
};

const merchantProfileKey = () => `get:user/${currentRestaurantId()}`;

const ok = (config, data, status = 200) => ({
  data,
  status,
  statusText: status === 201 ? "Created" : "OK",
  headers: {},
  config,
  offline: true,
});

// A rejection shaped like a server response, so callers' err.response.data.message works
const fail = (config, status, message) =>
  Promise.reject(
    new AxiosError(message, "ERR_OFFLINE_REJECTED", config, null, {
      status,
      statusText: "Offline",
      data: { message },
      headers: {},
      config,
    })
  );

// Short-circuit a request so it is handled by the offline layer instead of the network
const forceLocal = (config, reason) => {
  config.adapter = () => Promise.reject(new AxiosError(reason, "ERR_LOCAL_PENDING", config));
  return config;
};

const isOfflineError = (error) => {
  if (error.code === "ERR_LOCAL_PENDING") return true;
  const status = error.response?.status;
  if (status) return [502, 503, 504].includes(status);
  return (
    !navigator.onLine ||
    error.code === "ERR_NETWORK" ||
    error.code === "ECONNABORTED" ||
    error.code === "ETIMEDOUT" ||
    error.message === "Network Error"
  );
};

const enqueue = async (action) => {
  await queueSyncAction({ ...action, restaurantId: currentRestaurantId() });
  if (navigator.onLine) window.dispatchEvent(new Event("cloudrasoi-sync-request"));
};

// Queued actions for this restaurant (legacy actions without restaurantId included)
const pendingActions = async () => {
  const rid = currentRestaurantId();
  const queue = await getQueuedActions();
  return queue.filter((a) => !a.restaurantId || a.restaurantId === rid);
};

// Every id (local and server) referenced by pending actions
const pendingOrderIds = async (queue) => {
  const ids = new Set();
  for (const a of queue) {
    for (const id of [a.orderId, a.localId, a.data?.sourceOrderId]) {
      if (!id) continue;
      ids.add(String(id));
      if (isLocalId(id)) {
        const serverId = await getIdMapping(id);
        if (serverId) ids.add(serverId);
      }
    }
  }
  return ids;
};

const ORDER_MUTATION_RE = /^(?:orders\/([^/]+)\/(?:items|cancel|close|kot-print|transfer-table|merge)|bill\/from-order\/([^/?]+))$/;

/*
 * While older offline actions are still waiting to sync, newer requests that touch
 * the same orders must go through the queue too — otherwise they reach the server
 * first and get overwritten when the older actions replay.
 */
const mustStayLocal = async (config) => {
  const method = (config.method || "get").toLowerCase();
  const path = pathOf(config).split("?")[0];
  if (!path.startsWith("orders/") && !path.startsWith("bill/from-order/")) return false;

  const queue = await pendingActions();
  if (queue.length === 0) return false;

  if (method === "get") {
    if (path === "orders/active") return true;
    const m = path.match(/^orders\/([^/]+)$/);
    if (!m || NON_ORDER_SEGMENTS.includes(m[1])) return false;
    const ids = await pendingOrderIds(queue);
    return ids.has(m[1]) || (config.localOrderId && ids.has(config.localOrderId));
  }

  // A table may already have an offline order that the server doesn't know about yet
  if (path === "orders/select-table") return true;

  const m = path.match(ORDER_MUTATION_RE);
  if (!m) return false;
  const ids = await pendingOrderIds(queue);
  const body = parseBody(config);
  return [m[1] || m[2], config.localOrderId, body.sourceOrderId].some((id) => id && ids.has(String(id)));
};

/* ─────────────────────────── request ─────────────────────────── */

api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Offline-created orders: point at the real server order once it has synced
    const localMatch = (config.url || "").match(LOCAL_ID_RE);
    if (localMatch) {
      const localId = localMatch[0];
      const serverId = await getIdMapping(localId);
      if (serverId) {
        config.url = config.url.replace(localId, serverId);
        config.localOrderId = localId;
      } else if (!config.skipOffline) {
        return forceLocal(config, "Order has not synced yet");
      }
    }

    const body = config.data && typeof config.data === "object" ? config.data : null;
    if (body && isLocalId(body.sourceOrderId)) {
      const serverId = await getIdMapping(body.sourceOrderId);
      if (serverId) {
        config.data = { ...body, sourceOrderId: serverId };
      } else if (!config.skipOffline) {
        return forceLocal(config, "Order has not synced yet");
      }
    }

    // The sync manager itself must always talk to the server
    if (!config.skipOffline && (await mustStayLocal(config))) {
      window.dispatchEvent(new Event("cloudrasoi-sync-request"));
      return forceLocal(config, "Waiting for earlier offline changes to sync");
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/* ─────────────────────────── offline GET ─────────────────────────── */

const offlineGet = async (config) => {
  const key = cacheKeyFor(config);
  if (!key) return null;

  let cached = await getCachedData(scoped(key));
  if (!cached && key.startsWith("order_") && config.localOrderId) {
    cached = await getCachedData(scoped(`order_${config.localOrderId}`));
  }
  if (cached) return ok(config, cached);

  // Unknown mobile while offline: treat as a new customer (the bill sync upserts it)
  if (key.startsWith("get:customers/check/")) return ok(config, { exists: false });

  return null;
};

/* ─────────────────────────── offline mutations ─────────────────────────── */

const loadActive = async () =>
  (await getCachedData(scoped("active_orders"))) || { tables: {}, parcels: [] };

const saveActive = (active) => cacheData(scoped("active_orders"), active);

const matchesIds = (entryId, ids) => ids.includes(String(entryId));

const removeFromActive = (active, ids) => {
  for (const key of Object.keys(active.tables || {})) {
    if (matchesIds(active.tables[key].orderId, ids)) delete active.tables[key];
  }
  active.parcels = (active.parcels || []).filter((p) => !matchesIds(p.orderId, ids));
};

const patchActiveEntry = (active, ids, patch) => {
  for (const key of Object.keys(active.tables || {})) {
    if (matchesIds(active.tables[key].orderId, ids)) Object.assign(active.tables[key], patch);
  }
  (active.parcels || []).forEach((p) => {
    if (matchesIds(p.orderId, ids)) Object.assign(p, patch);
  });
};

const loadOrder = async (orderId, config) => {
  for (const id of [orderId, config.localOrderId].filter(Boolean)) {
    const key = scoped(`order_${id}`);
    const order = await getCachedData(key);
    if (order) return { order, key };
  }
  return { order: null, key: scoped(`order_${orderId}`) };
};

// Same line identity and KOT bookkeeping as the server (utills/orderItems.js)
const lineKey = (i) =>
  [String(i.itemId), i.name || "", Number(i.price) || 0, i.variant?.name || "", (i.addOns || []).map((a) => a.name).join(",")].join("_");

const printedQty = (i) => i.kotQty ?? (i.kotPrinted ? i.qty : 0);

const recalcTotals = (order) => {
  order.subtotal = (order.items || []).reduce((sum, i) => sum + Number(i.itemTotal ?? i.qty * i.price), 0);
  order.totalAmount = order.subtotal;
};

const newOrderNumber = () => Math.floor(1000 + Math.random() * 9000);

const createTableOrder = async (config, body) => {
  const key = `${body.floorName}-${body.sectionName}-${body.roomType || "NA"}-${body.unitNumber}`;
  const active = await loadActive();

  const existingId = active.tables?.[key]?.orderId;
  if (existingId) {
    const { order } = await loadOrder(existingId, config);
    if (order) return ok(config, order);
  }

  const localId = "offline_ord_" + Date.now();
  const orderNumber = newOrderNumber();
  const mockOrder = {
    _id: localId,
    orderType: "DINE_IN",
    orderNumber,
    floorName: body.floorName,
    sectionName: body.sectionName,
    sectionType: body.sectionType,
    unitNumber: body.unitNumber,
    roomType: body.roomType || "NA",
    status: "OPEN",
    paymentStatus: "UNPAID",
    items: [],
    subtotal: 0,
    totalAmount: 0,
    kotRound: 0,
    source: "POS",
    createdAt: new Date().toISOString(),
  };

  await enqueue({ type: "CREATE_ORDER", localId, data: body });

  active.tables = active.tables || {};
  active.tables[key] = {
    orderId: localId,
    status: "OPEN",
    orderNumber,
    orderType: "DINE_IN",
    source: "POS",
    subtotal: 0,
    totalAmount: 0,
  };
  await saveActive(active);
  await cacheData(scoped(`order_${localId}`), mockOrder);

  return ok(config, mockOrder, 201);
};

const createParcel = async (config) => {
  const localId = "offline_ord_" + Date.now();
  const orderNumber = newOrderNumber();
  const mockOrder = {
    _id: localId,
    orderType: "DIRECT",
    orderNumber,
    isDirect: true,
    parcelToken: `P${orderNumber}`,
    status: "ORDER_PLACED",
    paymentStatus: "UNPAID",
    items: [],
    totalAmount: 0,
    subtotal: 0,
    gst: 0,
    discount: 0,
    kotRound: 0,
    source: "POS",
    createdAt: new Date().toISOString(),
  };

  await enqueue({ type: "CREATE_PARCEL", localId });

  const active = await loadActive();
  active.parcels = active.parcels || [];
  active.parcels.push({
    orderId: localId,
    orderNumber,
    token: `P${orderNumber}`,
    status: "ORDER_PLACED",
    source: "POS",
    orderType: "DIRECT",
    subtotal: 0,
    totalAmount: 0,
  });
  await saveActive(active);
  await cacheData(scoped(`order_${localId}`), mockOrder);

  return ok(config, mockOrder, 201);
};

const updateItems = async (config, body, orderId) => {
  const items = Array.isArray(body.items) ? body.items : [];
  const { order, key } = await loadOrder(orderId, config);

  if (order && ["CLOSED", "CANCELLED"].includes(order.status)) {
    return fail(config, 400, "Order cannot be modified");
  }

  await enqueue({ type: "UPDATE_ITEMS", orderId, items });

  if (!order) return ok(config, { success: true, items });

  // Same rule as the server: quantity the kitchen already has stays "sent"
  const sent = {};
  const sentAt = {};
  (order.items || []).forEach((i) => {
    const k = lineKey(i);
    sent[k] = (sent[k] || 0) + printedQty(i);
    if (i.kotPrintedAt && !sentAt[k]) sentAt[k] = i.kotPrintedAt;
  });

  order.items = items.map((item) => {
    const qty = Number(item.qty);
    const price = Number(item.price);
    const k = lineKey({ ...item, price });
    const kotQty = Math.min(qty, sent[k] || 0);
    sent[k] = (sent[k] || 0) - kotQty;
    return {
      ...item,
      qty,
      price,
      itemTotal: qty * price,
      kotQty,
      kotPrinted: kotQty >= qty,
      kotPrintedAt: kotQty > 0 ? sentAt[k] || null : null,
    };
  });
  if (items.length === 0) order.status = "OPEN";
  else if (order.status === "OPEN" || order.items.some((i) => printedQty(i) < i.qty)) order.status = "ORDER_PLACED";
  recalcTotals(order);
  await cacheData(key, order);

  const active = await loadActive();
  patchActiveEntry(active, [String(orderId), order._id, config.localOrderId].filter(Boolean), {
    status: order.status,
    subtotal: order.subtotal,
    totalAmount: order.totalAmount,
  });
  await saveActive(active);

  return ok(config, order);
};

const closeOutOrder = async (config, orderId, order, key, patch) => {
  const active = await loadActive();
  removeFromActive(active, [String(orderId), order?._id, config.localOrderId].filter(Boolean).map(String));
  await saveActive(active);
  if (order) await cacheData(key, { ...order, ...patch });
};

const cancelOrder = async (config, body, orderId) => {
  const { order, key } = await loadOrder(orderId, config);
  if (order?.status === "CANCELLED") return fail(config, 400, "Order already cancelled");
  if (order?.status === "CLOSED") return fail(config, 400, "Closed order cannot be cancelled");
  if (order?.paymentStatus === "PAID") return fail(config, 400, "Paid order cannot be cancelled");

  await enqueue({ type: "CANCEL_ORDER", orderId, reason: body.reason });
  await closeOutOrder(config, orderId, order, key, { status: "CANCELLED" });

  return ok(config, { success: true, message: "Order cancelled locally" });
};

const closeOrder = async (config, body, orderId) => {
  const { order, key } = await loadOrder(orderId, config);
  if (order?.paymentStatus === "PAID") return fail(config, 400, "Order already paid");

  await enqueue({ type: "CLOSE_ORDER", orderId, payment: body.payment });
  await closeOutOrder(config, orderId, order, key, { status: "CLOSED", paymentStatus: "PAID" });

  return ok(config, { success: true, message: "Order closed locally" });
};

const printKot = async (config, body, orderId) => {
  const { order, key } = await loadOrder(orderId, config);
  if (!order) return fail(config, 400, "Order details are not available offline");
  if (["CLOSED", "CANCELLED"].includes(order.status)) {
    return fail(config, 400, "Cannot KOT print a closed order");
  }

  const forceReprint = !!body.forceReprint;
  const newItems = (order.items || []).filter((i) => printedQty(i) < i.qty);

  if (newItems.length === 0 && !forceReprint) {
    return ok(config, {
      alreadyPrinted: true,
      kotRound: order.kotRound || 0,
      message: "All items already KOT printed",
      order,
    });
  }

  // Only the quantity the kitchen doesn't have yet
  const itemsToPrint = forceReprint
    ? order.items
    : newItems.map((i) => ({ ...i, qty: i.qty - printedQty(i), itemTotal: (i.qty - printedQty(i)) * i.price }));
  const now = new Date().toISOString();
  order.items = order.items.map((i) => ({
    ...i,
    kotQty: i.qty,
    kotPrinted: true,
    kotPrintedAt: printedQty(i) > 0 ? i.kotPrintedAt : now,
  }));
  if (!forceReprint) order.kotRound = (order.kotRound || 0) + 1;
  order.status = "PREPARING";
  await cacheData(key, order);

  // A reprint changes nothing on the server, so only real rounds are synced
  if (!forceReprint) await enqueue({ type: "KOT_PRINT", orderId });

  const active = await loadActive();
  patchActiveEntry(active, [String(orderId), order._id, config.localOrderId].filter(Boolean), { status: "PREPARING" });
  await saveActive(active);

  return ok(config, { alreadyPrinted: false, forceReprint, kotRound: order.kotRound, itemsToPrint, order });
};

const transferTable = async (config, body, orderId) => {
  if (!body.floorName || !body.sectionName || !body.unitNumber) {
    return fail(config, 400, "floorName, sectionName and unitNumber are required");
  }

  const ids = [String(orderId), config.localOrderId].filter(Boolean);
  const active = await loadActive();
  const targetKey = `${body.floorName}-${body.sectionName}-${body.roomType || "NA"}-${body.unitNumber}`;
  const sourceKey = Object.keys(active.tables || {}).find((k) => matchesIds(active.tables[k].orderId, ids));

  if (!sourceKey) return fail(config, 404, "Active dine-in order not found");
  if (active.tables[targetKey] && targetKey !== sourceKey) {
    return fail(config, 409, "Target table already has an active order");
  }

  await enqueue({ type: "TRANSFER_TABLE", orderId, data: body });

  active.tables[targetKey] = active.tables[sourceKey];
  if (targetKey !== sourceKey) delete active.tables[sourceKey];
  await saveActive(active);

  const { order, key } = await loadOrder(orderId, config);
  if (order) {
    Object.assign(order, {
      floorName: body.floorName,
      sectionName: body.sectionName,
      sectionType: body.sectionType || order.sectionType,
      unitNumber: body.unitNumber,
      roomType: body.roomType || "NA",
    });
    await cacheData(key, order);
  }

  return ok(config, { success: true, order });
};

const mergeOrders = async (config, body, targetId) => {
  const sourceId = body.sourceOrderId;
  if (!sourceId) return fail(config, 400, "sourceOrderId is required");
  if (String(sourceId) === String(targetId)) return fail(config, 400, "Cannot merge an order into itself");

  const { order: target, key: targetKey } = await loadOrder(targetId, config);
  const { order: source, key: sourceKey } = await loadOrder(sourceId, {});
  if (!target || !source) {
    return fail(config, 400, "Both orders must have been opened on this device to merge offline");
  }

  for (const [label, o] of [["Target", target], ["Source", source]]) {
    if (["CLOSED", "CANCELLED"].includes(o.status)) {
      return fail(config, 400, `${label} order is already ${o.status.toLowerCase()}`);
    }
    if (o.paymentStatus === "PAID") return fail(config, 400, `${label} order is already paid`);
  }

  await enqueue({ type: "MERGE_ORDERS", orderId: targetId, data: { sourceOrderId: sourceId } });

  // Same line-merging rule as the server
  const targetIndex = {};
  target.items = target.items || [];
  target.items.forEach((i, idx) => { targetIndex[lineKey(i)] = idx; });
  for (const item of source.items || []) {
    const idx = targetIndex[lineKey(item)];
    if (idx !== undefined) {
      const existing = target.items[idx];
      const sentQty = printedQty(existing) + printedQty(item);
      existing.qty += item.qty;
      existing.itemTotal = existing.qty * existing.price;
      existing.kotQty = sentQty;
      existing.kotPrinted = sentQty >= existing.qty;
    } else {
      target.items.push({ ...item });
    }
  }
  if (target.items.length > 0 && target.status === "OPEN") target.status = "ORDER_PLACED";
  recalcTotals(target);
  await cacheData(targetKey, target);
  await cacheData(sourceKey, { ...source, status: "CANCELLED" });

  const active = await loadActive();
  removeFromActive(active, [String(sourceId), source._id].map(String));
  patchActiveEntry(active, [String(targetId), target._id, config.localOrderId].filter(Boolean), {
    status: target.status,
    subtotal: target.subtotal,
    totalAmount: target.totalAmount,
  });
  await saveActive(active);

  return ok(config, { success: true, order: target });
};

// Mirrors billController.createBillFromOrder so the provisional slip matches the synced bill
const billFromOrder = async (config, body, orderId) => {
  const { order, key } = await loadOrder(orderId, config);
  if (!order) return fail(config, 400, "Order details are not available offline");
  if (order.paymentStatus === "PAID") return fail(config, 400, "Order already billed");
  if (!order.items?.length) return fail(config, 400, "Order has no items");

  // Loyalty points and wallet balances live on the server; they can't be applied offline
  if (body.redeemPoints || body.useWallet) {
    toast.warn("Loyalty points / wallet can't be applied offline — bill saved without them");
  }
  const data = { ...body, redeemPoints: false, useWallet: false };

  const gst = Number(data.gst) || 0;
  const discount = Number(data.discount) || 0;
  const flatDiscount = Number(data.flatDiscount) || 0;

  const subtotal = Number(order.subtotal) ||
    order.items.reduce((sum, i) => sum + Number(i.itemTotal ?? i.qty * i.price), 0);
  const discountAmount = flatDiscount > 0 ? Math.min(flatDiscount, subtotal) : (subtotal * discount) / 100;
  const discountPercentage = flatDiscount > 0 && subtotal > 0
    ? Math.round((discountAmount / subtotal) * 10000) / 100
    : discount;
  const taxableAmount = subtotal - discountAmount;
  const gstAmount = (taxableAmount * gst) / 100;
  let rawTotal = taxableAmount + gstAmount;

  const merchant = (await getCachedData(scoped(merchantProfileKey()))) || {};
  let platformFeePercentage = 0;
  let platformFeeAmount = 0;
  if (merchant.platformFee?.enabled && merchant.platformFee.percentage > 0) {
    platformFeePercentage = merchant.platformFee.percentage;
    platformFeeAmount = Math.round(((rawTotal * platformFeePercentage) / 100) * 100) / 100;
    rawTotal += platformFeeAmount;
  }
  const totalAmount = Math.round(rawTotal);
  const roundOff = parseFloat((totalAmount - rawTotal).toFixed(2));

  await enqueue({ type: "BILL_FROM_ORDER", orderId, data });

  const now = new Date();
  const mockBill = {
    _id: "mock_bill_" + now.getTime(),
    // Provisional number — the server assigns the real one when this bill syncs
    billId: `OFFLINE-${now.toTimeString().slice(0, 8).replace(/:/g, "")}`,
    isOffline: true,
    billDate: now.toISOString(),
    orderId,
    customerName: data.name,
    customerMobile: data.mobile,
    sectionName: order.sectionName,
    unitNumber: order.unitNumber,
    items: order.items,
    subtotal,
    gstPercentage: gst,
    gstAmount,
    discountPercentage,
    discountAmount,
    taxableAmount,
    platformFeePercentage,
    platformFeeAmount,
    roundOff,
    totalAmount,
    payment: {
      method: data.paymentMethod || "CASH",
      amountPaid: totalAmount,
      paidAt: now.toISOString(),
    },
    splitPayments: data.paymentMethod === "SPLIT" ? data.splitPayments || [] : [],
    invoiceType: order.orderType || "DINE_IN",
    restaurantId: {
      _id: currentRestaurantId(),
      shop_type: merchant.shop_type,
      address: merchant.address,
      mobile: merchant.mobile,
      publicMobile: merchant.publicMobile,
      upiId: merchant.upiId,
    },
  };

  await closeOutOrder(config, orderId, order, key, {
    status: "CLOSED",
    paymentStatus: "PAID",
    totalAmount,
  });

  return ok(config, mockBill, 201);
};

const MUTATION_HANDLERS = [
  { method: "post", re: /^orders\/select-table$/, run: (c, b) => createTableOrder(c, b) },
  { method: "post", re: /^orders\/parcel$/, run: (c) => createParcel(c) },
  { method: "patch", re: /^orders\/([^/]+)\/items$/, run: (c, b, id) => updateItems(c, b, id) },
  { method: "post", re: /^orders\/([^/]+)\/cancel$/, run: (c, b, id) => cancelOrder(c, b, id) },
  { method: "post", re: /^orders\/([^/]+)\/close$/, run: (c, b, id) => closeOrder(c, b, id) },
  { method: "post", re: /^orders\/([^/]+)\/kot-print$/, run: (c, b, id) => printKot(c, b, id) },
  { method: "patch", re: /^orders\/([^/]+)\/transfer-table$/, run: (c, b, id) => transferTable(c, b, id) },
  { method: "post", re: /^orders\/([^/]+)\/merge$/, run: (c, b, id) => mergeOrders(c, b, id) },
  { method: "post", re: /^bill\/from-order\/([^/?]+)$/, run: (c, b, id) => billFromOrder(c, b, id) },
];

/* ─────────────────────────── response ─────────────────────────── */

api.interceptors.response.use(
  (response) => {
    // Remember successful GETs so the POS can keep working offline
    const config = response.config || {};
    if ((config.method || "").toLowerCase() === "get" && response.status === 200) {
      const key = cacheKeyFor(config);
      if (key) cacheData(scoped(key), response.data);
    }
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) return Promise.reject(error);

    const config = error.config || {};
    const status = error.response?.status;
    const url = config.url || "";
    const method = (config.method || "").toLowerCase();
    const token = localStorage.getItem('token');

    // Handle 401 Unauthorized
    if (status === 401 && token && !url.includes("/public")) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
      return Promise.reject(error);
    }

    // The sync manager handles its own failures
    if (config.skipOffline) return Promise.reject(error);

    // Handle 403 Subscription Inactive or Expired
    if (status === 403 && (error.response?.data?.code === "SUBSCRIPTION_REQUIRED" || error.response?.data?.code === "SUBSCRIPTION_EXPIRED")) {
      toast.error(error.response.data.message || "Subscription required!");
      setTimeout(() => {
        window.location.hash = "/subscription-plans";
      }, 500);
      return Promise.reject(error);
    }

    if (!isOfflineError(error)) return Promise.reject(error);

    console.log(`[API Offline Interceptor] Intercepted ${method.toUpperCase()} ${url}`);

    if (method === "get") {
      const cached = await offlineGet(config);
      if (cached) return cached;
      return Promise.reject(error);
    }

    const path = pathOf(config).split("?")[0];
    const handler = MUTATION_HANDLERS.find((h) => h.method === method && h.re.test(path));
    if (!handler) return Promise.reject(error);

    const match = path.match(handler.re);
    return handler.run(config, parseBody(config), match[1]);
  }
);

export default api;
